import React, { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams, useRouteMatch } from 'react-router-dom'

import { Layout as AntLayout, Skeleton } from 'antd'

import { StepStatus, Wizard, WizardStatus, WizardStep } from 'core/domain/wizard/wizard'

import Card from 'common/components/dataDisplay/Card'
import Tooltip from 'common/components/dataDisplay/Tooltip'
import Button from 'common/components/general/Button'
import Icon from 'common/components/general/Icon'
import { pushState } from 'common/components/utils/WindowUtils'

import { ROUTE_PATHS } from '../../../../common/routePaths'
import { DESIGN_BENEFIT_PATH } from '../index'
import { disclaimerModal } from './DisclaimerModal'
import { HeaderNavigation } from './HeaderNavigation'
import { NavItemProps } from './models'

import './DesignStyles.less'

const { Content, Header, Footer } = AntLayout

enum WizardButton {
  Next = 'next',
  Finish = 'finish',
  Back = 'back',
  Save = 'save',
}

declare type BackButtonType = WizardButton.Back
declare type NextButtonType = WizardButton.Next | WizardButton.Finish | WizardButton.Save

type DesignBenefitProps = {
  navItemsMapping: NavItemProps[]
  readonlyStepsWhenBenefitIsFinished: string[]
  getBenefitDesignWizard: (planPeriodId: string) => Promise<Wizard>
  getBenefitDesignWizardState: (planPeriodId: string) => Promise<any>
  onFinish?: (planPeriodId: string) => void
  onDone?: () => void
}

const DesignBenefitWizard: React.FC<DesignBenefitProps> = (props) => {
  const history = useHistory()
  const match = useRouteMatch()
  const { planPeriod, step } = useParams<any>()
  const benefitDetailsFullPath = match.path.substring(0, match.path.indexOf(DESIGN_BENEFIT_PATH))
  const [navigationItems, setNavigationItems] = useState<WizardStep[]>([])
  const [currentNavigationItem, setCurrentNavigationItem] = useState<NavItemProps>()
  const [benefitDesignState, setBenefitDesignState] = useState<any>()
  const [isFormValid, setIsFormValid] = useState<boolean>(false)
  const [isSubmiting, setIsSubmiting] = useState<boolean>(false)
  const [hasDirtyData, setHasDirtyData] = useState<boolean>(false)
  const [overrideTooltip, setOverrideTooltip] = useState<string | undefined>(undefined)
  const { t: translate } = useTranslation()

  let goBackMethod: () => void
  let goNextMethod: () => void
  let getIndexOfActiveNavigationItemMethod: (items: WizardStep[]) => number
  let setEditingNavigationItemMethod: (editingStep: string | undefined) => void
  let submitFormHandler: () => Promise<any>

  useEffect(() => {
    props.getBenefitDesignWizard(planPeriod).then((response: Wizard) => {
      if (response) setNavigationItems(response.steps)
    })
  }, [])

  useEffect(() => {
    try {
      if (navigationItems.length > 0) {
        if (step) {
          const getCurrentNavigationItem = navigationItems.find((item) => item.id === step)
          if (!getCurrentNavigationItem)
            throw new Error(translate('admin-dashboard.benefit-details.class-design.errors.navigation-item-not-found'))
          if (getCurrentNavigationItem.status !== StepStatus.Disabled) {
            const navigationItemProps = props.navItemsMapping.find((navItem) => navItem.key === step)
            setEditingNavigationItemMethod(step)
            setCurrentNavigationItem(navigationItemProps)
          } else {
            loadActiveNavigationItem(navigationItems)
          }
        } else {
          loadActiveNavigationItem(navigationItems)
        }
      }
    } catch (e) {
      console.log(e)
    }
  }, [navigationItems])

  useEffect(() => {
    if (currentNavigationItem) {
      setHasDirtyData(false)
      setIsSubmiting(false)
      setOverrideTooltip(undefined)

      pushState(`${benefitDetailsFullPath}${DESIGN_BENEFIT_PATH}/${planPeriod}/${currentNavigationItem?.key}`)

      props.getBenefitDesignWizardState(planPeriod).then((response) => {
        if (response.isBenefitEditable === false) {
          history.push(benefitDetailsFullPath)
        } else {
          setBenefitDesignState(response)
        }
      })
    }
  }, [currentNavigationItem])

  const loadActiveNavigationItem = (items: WizardStep[]) => {
    const index = getIndexOfActiveNavigationItemMethod(items)
    const navigationItem = items[index]
    const navigationItemProps = props.navItemsMapping.find((navItem) => navItem.key === navigationItem.id)
    setCurrentNavigationItem(navigationItemProps)
  }

  const getBackButton = (navigationItems: WizardStep[]): ReactNode => {
    if (navigationItems.length > 0 && currentNavigationItem && currentNavigationItem.key !== navigationItems[0].id) {
      return getButtonBasedOnType(WizardButton.Back, translate)
    }
    return null
  }

  const getNextButton = (navigationItems: WizardStep[]): ReactNode => {
    if (navigationItems.length > 0 && currentNavigationItem) {
      if (
        currentNavigationItem.key === navigationItems[navigationItems.length - 1].id &&
        benefitDesignState?.wizardStatus === WizardStatus.Completed &&
        !props.onDone
      ) {
        return null
      }

      let buttonType: NextButtonType = WizardButton.Next
      if (currentNavigationItem.key === navigationItems[navigationItems.length - 1].id) {
        buttonType =
          props.onDone && benefitDesignState?.wizardStatus === WizardStatus.Completed
            ? WizardButton.Save
            : WizardButton.Finish
      }

      const button = getButtonBasedOnType(buttonType, translate)
      let tooltip: string | undefined = overrideTooltip
        ? overrideTooltip
        : translate(currentNavigationItem.nextButtonTooltip ?? '')

      if (isCurrentNavigationItemReadOnly()) {
        tooltip = translate('admin-dashboard.benefit-details.class-design.current-navigation-read-only-tooltip')
      }

      if (isNextButtonDisabled()) {
        return (
          <Tooltip title={tooltip ?? ''} position="left">
            {button}
          </Tooltip>
        )
      } else {
        return button
      }
    }
    return null
  }

  const getButtonBasedOnType = (type: BackButtonType | NextButtonType, translate: any) => {
    switch (type) {
      case WizardButton.Back:
        return (
          <Button
            size="default"
            icon="fa fa-arrow-left"
            htmlType="button"
            type="default"
            className={'t-wizard-next-back'}
            onClick={backHandler}
          >
            {translate('admin-dashboard.benefit-details.class-design.back-button')}
          </Button>
        )
      case WizardButton.Next:
        return (
          <Button
            icon="fa fa-arrow-right"
            iconPosition="right"
            size="default"
            htmlType="submit"
            type="primary"
            disabled={isNextButtonDisabled()}
            onClick={submitHandler}
            className="right t-wizard-next-btn"
            loading={isSubmiting}
          >
            {isSubmiting
              ? translate('admin-dashboard.benefit-details.class-design.saving-button')
              : translate('admin-dashboard.benefit-details.class-design.next-button')}
          </Button>
        )
      case WizardButton.Finish: {
        return (
          <Button
            icon="fa fa-arrow-right"
            iconPosition="right"
            size="default"
            htmlType="submit"
            type="primary"
            disabled={isNextButtonDisabled()}
            onClick={submitHandler}
            className="right t-wizard-next-finish"
            loading={isSubmiting}
          >
            {isSubmiting
              ? translate('admin-dashboard.benefit-details.class-design.saving-button')
              : translate('admin-dashboard.benefit-details.class-design.finish-button')}
          </Button>
        )
      }
      case WizardButton.Save: {
        return (
          <Button
            icon="fa fa-arrow-right"
            iconPosition="right"
            size="default"
            htmlType="submit"
            type="primary"
            // disabled={isNextButtonDisabled()}
            onClick={submitSaveHandler}
            className="right t-wizard-next-finish"
            loading={isSubmiting}
          >
            {translate('admin-dashboard.benefit-details.class-design.save-button')}
          </Button>
        )
      }
    }
  }

  const isNextButtonDisabled = (): boolean => {
    if (!isFormValid || isCurrentNavigationItemReadOnly()) {
      return true
    }
    return false
  }

  const isCurrentNavigationItemReadOnly = (): boolean => {
    if (!benefitDesignState || !currentNavigationItem) {
      return false
    } else if (
      benefitDesignState.wizardStatus === WizardStatus.Completed &&
      props.readonlyStepsWhenBenefitIsFinished.some((item) => item === currentNavigationItem.key)
    ) {
      return true
    } else {
      return false
    }
  }

  const backHandler = () => {
    if (hasDirtyData) {
      disclaimerModal(() => {
        goBackMethod()
      }, translate)
    } else {
      goBackMethod()
    }
  }

  const submitHandler = () => {
    setIsSubmiting(true)
    submitFormHandler()
  }

  const submitSaveHandler = () => {
    setIsSubmiting(false)
    history.push(ROUTE_PATHS.ADMIN_DASHBOARD_PATH)
  }

  const goNext = () => {
    goNextMethod()
  }

  const finish = () => {
    setIsSubmiting(false)
    if (props.onFinish) {
      props.onFinish(planPeriod)
    } else {
      history.push(ROUTE_PATHS.ADMIN_DASHBOARD_PATH)
    }
  }

  const getBenefitDetailsTitleContent = (): ReactNode => {
    return (
      <div className="flex">
        <Button
          type="default"
          size="small"
          className="mr-8"
          onClick={() => {
            history.push(`${ROUTE_PATHS.ADMIN_DASHBOARD_PATH}`)
          }}
        >
          <Icon type="fa fa-arrow-left" fontAwesome={true} />
        </Button>
        <h5>Design your benefit</h5>
      </div>
    )
  }

  return (
    <Card title={getBenefitDetailsTitleContent()} className="benefit_details__design">
      <AntLayout className="wizard_content">
        <Header className="wizard_header">
          <HeaderNavigation
            navItemsMapping={props.navItemsMapping}
            translate={translate}
            navigationItems={navigationItems}
            hasDirtyData={hasDirtyData}
            setCurrentNavigationItem={setCurrentNavigationItem}
            bindGoBack={(goBack: () => void) => {
              goBackMethod = goBack
            }}
            bindGoNext={(goNext: () => void) => {
              goNextMethod = goNext
            }}
            bindGetIndexOfActiveNavigationItem={(getIndexOfActiveNavigationItem: (items: WizardStep[]) => number) => {
              getIndexOfActiveNavigationItemMethod = getIndexOfActiveNavigationItem
            }}
            bindSetEditingNavigationItem={(setEditingItem: (editingStep: string | undefined) => void) => {
              setEditingNavigationItemMethod = setEditingItem
            }}
            showDisclaimerModal={disclaimerModal}
          />
        </Header>

        <Content className="wizard_body">
          <Skeleton loading={!currentNavigationItem || !benefitDesignState} active>
            {currentNavigationItem ? (
              <currentNavigationItem.component
                name={currentNavigationItem.key}
                setIsFormValid={setIsFormValid}
                setIsSubmiting={setIsSubmiting}
                showAsReadOnly={isCurrentNavigationItemReadOnly()}
                setOverrideTooltip={setOverrideTooltip}
                bindSubmitFormHandler={(submitForm: any) => {
                  submitFormHandler = submitForm
                }}
                state={currentNavigationItem.mapState(benefitDesignState)}
                goNext={goNext}
                finish={finish}
                hasDirtyData={setHasDirtyData}
              />
            ) : null}
          </Skeleton>
        </Content>

        <Footer className="wizard_footer">
          {benefitDesignState ? (
            <>
              {getBackButton(navigationItems)} {getNextButton(navigationItems)}
            </>
          ) : null}
        </Footer>
      </AntLayout>
    </Card>
  )
}

export default DesignBenefitWizard
